import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Outil de prévision de trésorerie : simulez vos flux en quelques clics | Trezoone',
    description: 'Utilisez l\'outil de prévision de trésorerie Trezoone pour simuler vos flux futurs, identifier les tensions et anticiper vos besoins de financement.',
    canonical: '/outil-prevision-tresorerie',
    keywords: 'outil prévision trésorerie, outil de trésorerie, simulateur trésorerie, outil cash flow, outil prévisionnel',
  },
  hero: {
    tag: 'Outil prévision trésorerie',
    title: 'Simulez votre trésorerie future. Identifiez les tensions. Agissez avant.',
    subtitle: 'L\'outil de prévision Trezoone part de vos données réelles pour projeter votre cash sur 12 mois. Vous voyez immédiatement les mois à risque — et combien de temps vous avez pour réagir.',
    ctaLabel: 'Utiliser l\'outil gratuitement',
  },
  pilierLink: {
    label: 'En savoir plus : prévision de trésorerie',
    href: '/prevision-tresorerie',
  },
  sections: [
    {
      type: 'hook',
      title: 'Votre trésorerie dans 3 mois — vous le savez vraiment ?',
      content: 'La plupart des dirigeants ont une vague idée de leur solde actuel. Mais dans 3 mois ? Après le paiement des cotisations, avec un client qui paie en retard et une charge exceptionnelle ?',
      items: [
        'Sans outil de prévision : vous gérez au fil de l\'eau, en mode pompier',
        'Avec un outil de prévision : vous voyez le problème 6 semaines à l\'avance et vous avez le temps d\'agir',
      ],
    },
    {
      type: 'explain',
      title: 'Comment fonctionne l\'outil de prévision Trezoone',
      content: 'L\'outil de prévision de Trezoone combine données réelles et projections :',
      items: [
        'Étape 1 : Importez vos données bancaires (CSV, OFX) — 5 minutes',
        'Étape 2 : Renseignez vos recettes attendues et charges prévues',
        'Étape 3 : L\'outil calcule votre solde mois par mois sur 12 mois',
        'Étape 4 : Vous voyez les mois en vert (solde positif) et les mois en rouge (tension)',
        'Étape 5 : Simulez différents scénarios pour préparer vos décisions',
      ],
    },
    {
      type: 'example',
      title: 'Scénario : impact d\'un retard de subvention',
      content: 'Vous attendez une subvention de 15 000 € en avril. L\'outil simule ce qui se passe si elle arrive en juin à la place.',
      items: [
        'Scénario nominal : solde avril +8 000 € → tout va bien',
        'Scénario retard : solde avril -7 000 €, mai -14 000 € → tension critique',
        'Action identifiée : découvert autorisé de 15 000 € à négocier avec la banque en janvier',
        'Coût de la prévision : 2 heures de travail + une conversation bancaire proactive',
        'Coût sans prévision : frais de découvert, stress, relation bancaire dégradée',
      ],
    },
    {
      type: 'midcta',
      title: '2h de travail pour anticiper une tension de 7 000 € — simulez maintenant',
      content: 'Essai gratuit 14 jours · Scénarios multiples · Export PDF pour votre banque',
    },
    {
      type: 'solution',
      title: 'L\'outil Trezoone vs les alternatives',
      content: 'Pourquoi choisir Trezoone plutôt qu\'Excel ou un outil bancaire ?',
      items: [
        'Connecté à votre banque → données toujours à jour, sans ressaisie',
        'Scénarios multiples → comparez facilement les hypothèses',
        'Alertes automatiques → vous êtes notifié avant d\'atteindre les seuils critiques',
        'Interface pensée pour les non-financiers → pas de formation nécessaire',
        'Historique et apprentissage → l\'outil s\'améliore avec vos données',
        'Export PDF pour vos partenaires → un rapport professionnel en 2 clics',
      ],
    },
    {
      type: 'faq',
      title: 'FAQ',
      faq: [
        {
          q: 'Faut-il des compétences financières pour utiliser l\'outil ?',
          a: 'Non. L\'outil guide l\'utilisateur étape par étape. Si vous savez lire un relevé de compte et estimer vos prochaines recettes, vous pouvez l\'utiliser.',
        },
        {
          q: 'Peut-on simuler plusieurs scénarios en parallèle ?',
          a: 'Oui. Trezoone permet de créer plusieurs versions de votre plan de trésorerie (optimiste, réaliste, pessimiste) et de les comparer visuellement.',
        },
        {
          q: 'L\'outil est-il disponible sur mobile ?',
          a: 'Oui. Trezoone est accessible depuis tous les appareils (ordinateur, tablette, smartphone) via navigateur web.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Outils et simulations de trésorerie',
      links: [
        { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
        { label: 'Logiciel prévision trésorerie', href: '/logiciel-prevision-tresorerie' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Simulateur trésorerie', href: '/simulateur-tresorerie-association' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
        { label: 'Tableau trésorerie gratuit', href: '/tableau-tresorerie-gratuit' },
        { label: 'Modèle plan trésorerie', href: '/modele-plan-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'Lancez votre première simulation de trésorerie',
      content: 'Gratuit 14 jours. Résultats en moins d\'une heure.',
    },
  ],
  relatedLinks: [
    { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function OutilPrevisionTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
